import { Target, Eye, Award, HeartPulse, Sparkles, ShieldCheck } from "lucide-react";
import { CLINIC } from "../lib/clinic";

const HIGHLIGHTS = [
  {
    icon: Award,
    title: "Trusted Care",
    desc: "Years of experience serving families in Manjeri and beyond.",
  },
  {
    icon: HeartPulse,
    title: "Experienced Dentists",
    desc: "A team of qualified doctors with specialized expertise.",
  },
  {
    icon: Sparkles,
    title: "Modern Equipment",
    desc: "State-of-the-art tools for accurate, comfortable treatment.",
  },
  {
    icon: ShieldCheck,
    title: "Hygienic Environment",
    desc: "Fully sterilized instruments and safe treatment rooms.",
  },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <div className="lg:col-span-5 reveal">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#009688]/10 text-[#009688] text-xs font-semibold tracking-wide uppercase">
              About the Clinic
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F2937] leading-[1.05]">
              Comprehensive dental care, with a{" "}
              <span className="text-[#009688]">gentle touch</span>.
            </h2>
            <p className="mt-6 text-[17px] text-[#1F2937]/70 leading-relaxed">
              At <span className="font-semibold text-[#1F2937]">{CLINIC.name}</span>{" "}
              <span className="font-malayalam text-[#009688]">
                ({CLINIC.nameMl})
              </span>
              , we provide comprehensive dental care using modern equipment and
              patient-friendly treatments. Our goal is to deliver painless,
              affordable, and high-quality dental services for children and adults.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-gradient-to-br from-[#009688] to-[#00796b] text-white p-5 shadow-lg shadow-[#009688]/20">
                <Target className="w-6 h-6" />
                <div className="mt-3 text-xs uppercase tracking-wider opacity-80 font-semibold">
                  Our Mission
                </div>
                <p className="mt-1.5 text-sm leading-relaxed">
                  Deliver painless, honest and affordable dentistry for every family.
                </p>
              </div>
              <div className="rounded-2xl bg-white border border-[#009688]/15 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <Eye className="w-6 h-6 text-[#009688]" />
                <div className="mt-3 text-xs uppercase tracking-wider text-[#1F2937]/50 font-semibold">
                  Our Vision
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-[#1F2937]/80">
                  To be Manjeri&apos;s most trusted dental care destination.
                </p>
              </div>
            </div>
          </div>

          {/* Right column: image + highlights */}
          <div className="lg:col-span-7 grid gap-5 reveal">
            <div className="relative rounded-3xl overflow-hidden aspect-[16/10] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]">
              <img
                src="https://images.pexels.com/photos/5355921/pexels-photo-5355921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern dental treatment room at Abhayam Dental Clinic"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#009688]/30 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                <div className="glass rounded-2xl px-5 py-3">
                  <div className="text-[11px] uppercase tracking-wider text-[#1F2937]/60 font-semibold">
                    Since
                  </div>
                  <div className="text-2xl font-bold text-[#009688]">Est. Care</div>
                </div>
                <div className="glass rounded-2xl px-5 py-3 text-right">
                  <div className="text-[11px] uppercase tracking-wider text-[#1F2937]/60 font-semibold">
                    Happy Smiles
                  </div>
                  <div className="text-2xl font-bold text-[#009688]">1000+</div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  data-testid={`about-highlight-${title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group rounded-2xl bg-white border border-[#009688]/10 p-5 hover:border-[#009688]/30 hover:-translate-y-0.5 transition-all shadow-[0_4px_20px_rgb(0,0,0,0.02)] hover:shadow-[0_12px_30px_rgb(0,0,0,0.06)]"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#009688]/10 grid place-items-center group-hover:bg-[#009688] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5 text-[#009688] group-hover:text-white transition-colors" />
                  </div>
                  <div className="mt-4 font-semibold text-[#1F2937]">{title}</div>
                  <div className="mt-1 text-sm text-[#1F2937]/65 leading-relaxed">
                    {desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
